// ─────────────────────────────────────────────────────────────────────────────
// OKR Plans (coach) — formatting (same helpers as the SME side). The BE sends calendar dates ("2026-12-31") and ISO
// timestamps (BE §2.0); screens show "28 Nov", "Wed, 29 Oct", "Q4 2026".
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "2026-12-31" (calendar date, BE §2.0) or an ISO timestamp → local Date.
export const toDate = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Date → "2026-12-31".
export const toIsoDay = (date) => {
  const d = toDate(date) || date;
  if (!(d instanceof Date)) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// "31 Dec" (adds the year when it isn't `year`, default the current one).
export const day = (value, year = new Date().getFullYear()) => {
  const d = toDate(value);
  if (!d) return "";
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === year ? base : `${base} ${d.getFullYear()}`;
};

// "Wed, 29 Oct".
export const weekday = (value) => {
  const d = toDate(value);
  return d ? `${WEEKDAYS[d.getDay()]}, ${day(d, d.getFullYear())}` : "";
};

// "23–29 Nov" / "30 Nov – 6 Dec".
export const dayRange = (from, to) => {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) return "";
  if (a.getMonth() === b.getMonth())
    return `${a.getDate()}–${b.getDate()} ${MONTHS[b.getMonth()]}`;
  return `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]}`;
};

// Quarter row (BE 2.1) → "Q4 2026" and "1 Oct – 31 Dec 2026". Labels come as
// "Oct'26 - Dec'26", so the calendar quarter is derived from `start_date`
// (§8.5 question 3).
// A company quarter can start in any month: "Q4 2026" only when it lines up
// with a calendar quarter, otherwise its months ("Sep – Nov 2026").
export const quarterName = (q) => {
  const d = toDate(q?.start_date);
  if (!d) return q?.label || "";
  if (d.getMonth() % 3 === 0)
    return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  const end = toDate(q?.end_date);
  return end
    ? `${MONTHS[d.getMonth()]} – ${MONTHS[end.getMonth()]} ${end.getFullYear()}`
    : q?.label || "";
};
export const quarterRange = (q) => {
  const a = toDate(q?.start_date);
  const b = toDate(q?.end_date);
  if (!a || !b) return q?.label || "";
  return `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]} ${b.getFullYear()}`;
};
// CD36 / SME D46: reports are named by week — "Week 3 report".
export const reportName = (n) => (n ? `Week ${n} report` : "Weekly report");

export const firstNameOf = (name = "") => String(name).split(" ")[0] || "";

export const initialsOf = (name = "") =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const plural = (n, one, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

// D61 / CD40: a report row looks like the plan page — a number badge and the
// statement, never the words "Key result 2" / "Initiative 4". The BE label
// starts with that prefix (+ quotes or "·"); we split it into `num` + `text`.
// A label without it (e.g. the objective status) comes back as `text` only.
export const itemParts = (label) => {
  const raw = String(label ?? "");
  const m = raw.match(
    /^\s*(?:key\s*results?|initiatives?|kr|ini)\s*(\d+)\s*[·:\-–—]?\s*(.*)$/i,
  );
  if (!m || !m[2].trim()) return { num: null, text: raw };
  return {
    num: m[1],
    text: m[2]
      .trim()
      .replace(/^["“'‘](.*)["”'’]$/s, "$1")
      .trim(),
  };
};

// D75 / CD52: a report row's statement — the full one from the plan when the
// row's `target_id` is one of its key results / initiatives (the BE label can
// be cut short), else the label without its "Key result N" prefix.
export const rowStatement = (row, plan) => {
  const list =
    row?.target_type === "KR"
      ? plan?.key_results
      : row?.target_type === "INITIATIVE"
        ? plan?.initiatives
        : null;
  const idKey = row?.target_type === "KR" ? "key_result_id" : "initiative_id";
  const item = (list || []).find(
    (x) => String(x[idKey]) === String(row.target_id),
  );
  return item?.statement || itemParts(row?.label).text;
};
